import './DrugsLibraryDrawer.css';
import type {
  DrugManifestEntry,
  DrugCategory,
} from '../../services/host/DrugsServiceModule';
import type {
  DrugsLibraryState,
  DrugsLibraryTab,
} from '../../hooks/useDrugsLibrary';

/**
 * DrugsLibraryDrawer — 2026-06-08 Phase 4E wire-up for the canonical
 * Q Manual VG35002 drugs + poisons catalog.
 *
 * 2-tab layout: Drugs (10) / Poisons (11). The 2 seed categories map 1:1
 * to the 2 tabs (no super-category clustering needed).
 *
 * Detail expansion: name + Q Manual page citation + description + Q
 * evaluation + game information. Drugs + poisons are narrative-heavy
 * (Q Manual §16-§17 describes onset times, antidotes, lethality classes)
 * so the detail block is prose-focused, no stat grid.
 *
 * Mirrors GadgetsLibraryDrawer.tsx pattern (Phase 3E).
 */

const TAB_DEFS: ReadonlyArray<{ key: DrugsLibraryTab; label: string }> = [
  { key: 'drug', label: 'Drugs' },
  { key: 'poison', label: 'Poisons' },
];

const TAB_TO_CATEGORY: Record<DrugsLibraryTab, DrugCategory> = {
  drug: 'drug',
  poison: 'poison',
};

export interface DrugsLibraryDrawerProps {
  drugs: DrugManifestEntry[];
  state: DrugsLibraryState;
  error?: string | null;
  activeTab: DrugsLibraryTab;
  onTabChange: (tab: DrugsLibraryTab) => void;
  selectedDrugId: string | null;
  selectedDrugData: Record<string, unknown> | null;
  selectedDrugState: DrugsLibraryState;
  onSelectDrug: (drugId: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function DrugDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const description = (data['description'] as string) ?? '';
  const qEvaluation = (data['q_evaluation'] as string) ?? '';
  const gameInformation = (data['game_information'] as string) ?? '';
  const page = data['page'] as number | undefined;
  const manifestSection = data['manifest_section'] as string | undefined;

  return (
    <div className="br-drugs-detail" data-testid={`${testidPrefix}-detail`}>
      <h3 className="br-drugs-detail-title">
        {(data['name'] as string) ?? '—'}
      </h3>
      {(page !== undefined || manifestSection !== undefined) && (
        <p
          className="br-drugs-detail-page br-label"
          data-testid={`${testidPrefix}-detail-page`}
        >
          {manifestSection !== undefined && <>§{manifestSection} · </>}
          {page !== undefined && <>p.{page}</>}
        </p>
      )}
      {description !== '' && (
        <p
          className="br-drugs-detail-text"
          data-testid={`${testidPrefix}-detail-description`}
        >
          {description}
        </p>
      )}
      {qEvaluation !== '' && (
        <p
          className="br-drugs-detail-text br-drugs-detail-q"
          data-testid={`${testidPrefix}-detail-q-evaluation`}
        >
          <em>Q evaluation:</em> {qEvaluation}
        </p>
      )}
      {gameInformation !== '' && (
        <p
          className="br-drugs-detail-text br-drugs-detail-game"
          data-testid={`${testidPrefix}-detail-game-info`}
        >
          <strong>Game info:</strong> {gameInformation}
        </p>
      )}
    </div>
  );
}

export function DrugsLibraryDrawer({
  drugs,
  state,
  error,
  activeTab,
  onTabChange,
  selectedDrugId,
  selectedDrugData,
  selectedDrugState,
  onSelectDrug,
  onClose,
  testidPrefix = 'br-drugs-library',
}: DrugsLibraryDrawerProps) {
  const targetCategory = TAB_TO_CATEGORY[activeTab];
  const tabDrugs = drugs.filter((d) => d.category === targetCategory);

  return (
    <aside
      className="br-scenario-drawer br-drugs-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
      data-active-tab={activeTab}
    >
      <header className="br-drugs-library-header">
        <span className="br-drugs-library-title">Drugs &amp; Poisons</span>
        <span
          className="br-drugs-library-count br-label"
          data-testid={`${testidPrefix}-count`}
        >
          {tabDrugs.length}
        </span>
        {onClose !== undefined && (
          <button
            type="button"
            className="br-drugs-library-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close drugs library"
          >
            ✕
          </button>
        )}
      </header>

      <div
        className="br-drugs-library-tabs"
        role="tablist"
        data-testid={`${testidPrefix}-tabs`}
      >
        {TAB_DEFS.map((tabDef) => {
          const isActive = tabDef.key === activeTab;
          return (
            <button
              key={tabDef.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`${testidPrefix}-tab-${tabDef.key}`}
              data-active={isActive ? 'true' : 'false'}
              className={
                isActive
                  ? 'br-drugs-library-tab br-drugs-library-tab-active'
                  : 'br-drugs-library-tab'
              }
              onClick={() => onTabChange(tabDef.key)}
            >
              {tabDef.label}
            </button>
          );
        })}
      </div>

      <p
        className="br-drugs-library-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Q Manual VG35002 §16-§17 drugs + poisons catalog — 21 entries split
        10 Drugs + 11 Poisons. Click a row to expand its description + Q
        evaluation + game-mechanics rules (onset, antidote, lethality).
      </p>

      {state === 'loading' && (
        <p
          className="br-drugs-library-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading drugs…
        </p>
      )}

      {state === 'error' && (
        <p
          className="br-drugs-library-error"
          data-testid={`${testidPrefix}-error`}
        >
          Failed to load drugs: {error ?? 'unknown error'}
        </p>
      )}

      {state === 'ready' && tabDrugs.length === 0 && (
        <p
          className="br-drugs-library-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No {activeTab}s in seed.
        </p>
      )}

      {state === 'ready' && tabDrugs.length > 0 && (
        <div
          className="br-drugs-library-list"
          data-testid={`${testidPrefix}-list`}
        >
          <ul className="br-drugs-library-rows">
            {tabDrugs.map((d) => {
              const isSelected = d.id === selectedDrugId;
              return (
                <li key={d.id} className="br-drugs-library-row-wrapper">
                  <button
                    type="button"
                    className={
                      isSelected
                        ? 'br-drugs-library-row br-drugs-library-row-selected'
                        : 'br-drugs-library-row'
                    }
                    data-testid={`${testidPrefix}-row-${d.id}`}
                    data-selected={isSelected ? 'true' : 'false'}
                    onClick={() => onSelectDrug(d.id)}
                    aria-pressed={isSelected}
                  >
                    <span className="br-drugs-library-row-title">{d.name}</span>
                    {(d.manifest_section !== null || d.page !== null) && (
                      <span className="br-drugs-library-row-meta br-label">
                        {d.manifest_section !== null && (
                          <>§{d.manifest_section}</>
                        )}
                        {d.page !== null && <> · p.{d.page}</>}
                      </span>
                    )}
                  </button>
                  {isSelected && selectedDrugState === 'loading' && (
                    <p
                      className="br-drugs-library-detail-loading"
                      data-testid={`${testidPrefix}-detail-loading`}
                    >
                      Loading…
                    </p>
                  )}
                  {isSelected && selectedDrugState === 'error' && (
                    <p
                      className="br-drugs-library-detail-error"
                      data-testid={`${testidPrefix}-detail-error`}
                    >
                      Could not load drug.
                    </p>
                  )}
                  {isSelected &&
                    selectedDrugState === 'ready' &&
                    selectedDrugData !== null && (
                      <DrugDetail
                        data={selectedDrugData}
                        testidPrefix={testidPrefix}
                      />
                    )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </aside>
  );
}
